import dayjs from 'dayjs';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { HistoryPendingChangesActionType } from 'constants/pendingChanges';
import { getConditionalObject, replaceStringedBooleanValuesWithBoolean } from 'utils/data';
import { getCombinedPendingChangeKind, toPendingChangeKind } from 'utils/pendingChangeKind';
import { resolvePendingChangePropertyKey } from 'utils/pendingChanges';
import {
  buildFieldPendingChangeAuthors,
  getPendingChangeAuthorKey,
} from 'utils/pendingChangesAuthors';

import { selectLoanApplication } from './loanApplication.selectors';
import { selectPendingChangesHighlightAuthorKeys } from './pendingChangesAuthorsSelection.selectors';

const selectLoanChangesHistoryState = createSelector(
  selectLoanApplication,
  ({ loanChangesHistory }) => loanChangesHistory,
);

export const selectLoanChangesHistory = createSelector(
  selectLoanChangesHistoryState,
  ({ changes }) => changes,
);

const filterChangesByHighlightAuthors = (changes, highlightAuthorKeys) =>
  _.isNil(highlightAuthorKeys)
    ? changes
    : changes.filter(change => highlightAuthorKeys.includes(getPendingChangeAuthorKey(change)));

export const selectEntityChangedPropsById = memoizeOne(id =>
  createSelector(
    [selectLoanChangesHistory, selectPendingChangesHighlightAuthorKeys],
    (loanChangesHistory, highlightAuthorKeys) => {
      const entityLoanChangesHistory = filterChangesByHighlightAuthors(
        _.filter(loanChangesHistory, (__, key) => key.includes(id)).flat(),
        highlightAuthorKeys,
      );

      return entityLoanChangesHistory.reduce(
        (acc, { propertyName, newValue, oldValue, changeKind }) => {
          const { initialValue, changeKind: recordedChangeKind = null } = acc[propertyName] || {};
          const isInitialValueSet = _.has(acc, `${propertyName}.initialValue`);

          acc[propertyName] = {
            newValue,
            initialValue: isInitialValueSet ? initialValue : oldValue,
            changeKind: getCombinedPendingChangeKind([
              recordedChangeKind,
              toPendingChangeKind(changeKind),
            ]),
          };

          return acc;
        },
        {},
      );
    },
  ),
);

export const selectEntityLoanChangesByEntityTypeName = memoizeOne(entityTypeName =>
  createSelector(selectLoanChangesHistory, loanChangesHistory => {
    return Object.entries(loanChangesHistory).reduce((acc, [entityId, entityChanges]) => {
      if (entityChanges.some(change => change.entityTypeName === entityTypeName)) {
        acc[entityId] = entityChanges;
      }

      return acc;
    }, {});
  }),
);

export const selectEntitiesChangedPropsByIds = memoizeOne((entitiesIds, isStrictCheck = true) =>
  createSelector(
    [selectLoanChangesHistory, selectPendingChangesHighlightAuthorKeys],
    (loanChangesHistory, highlightAuthorKeys) =>
      Object.entries(loanChangesHistory).reduce((acc, [entityId, entityChanges]) => {
        const isEntityIncluded = isStrictCheck
          ? entitiesIds.includes(entityId)
          : entitiesIds.some(id => entityId.includes(id));

        if (!isEntityIncluded) {
          return acc;
        }

        const isWithAuthors = !_.isNil(highlightAuthorKeys);
        // Same author rows as the read-only views' dots (selectFieldPendingChangeAuthorsByEntityKey).
        const fieldAuthors = isWithAuthors
          ? buildFieldPendingChangeAuthors(entityChanges, highlightAuthorKeys)
          : null;

        const sortedEntityChanges = _.orderBy(
          filterChangesByHighlightAuthors(entityChanges, highlightAuthorKeys),
          [({ createdDate }) => dayjs(createdDate).valueOf()],
          ['asc'],
        );

        const primaryEntityActionType = sortedEntityChanges.findLast(
          ({ actionType, parentProperty }) =>
            // Nested entity changes (with parentProperty) cannot define the primary entity's action type
            !parentProperty &&
            [
              HistoryPendingChangesActionType.DELETED,
              HistoryPendingChangesActionType.ADDED,
            ].includes(actionType),
        )?.actionType;

        acc[entityId] = sortedEntityChanges.reduce((entityChangesAcc, change) => {
          const {
            entityTypeName,
            propertyName,
            newValue,
            oldValue,
            actionType: pendingChangeActionType,
            parentProperty,
            changeKind,
          } = change;

          const formattedPropertyName = resolvePendingChangePropertyKey({
            propertyName,
            parentProperty,
            entityTypeName,
          });

          const {
            initialValue,
            actionType: existsActionType,
            changeKind: recordedChangeKind = null,
          } = entityChangesAcc[formattedPropertyName] || {};

          const isInitialValueAlreadyExists = _.has(
            entityChangesAcc,
            `${formattedPropertyName}.initialValue`,
          );
          const isActionTypeAlreadyExists = _.has(
            entityChangesAcc,
            `${formattedPropertyName}.actionType`,
          );

          const actionTypeIfItAlreadyExists =
            pendingChangeActionType === HistoryPendingChangesActionType.DELETED
              ? pendingChangeActionType
              : existsActionType;

          entityChangesAcc[formattedPropertyName] = replaceStringedBooleanValuesWithBoolean({
            newValue,
            initialValue: isInitialValueAlreadyExists ? initialValue : oldValue,
            actionType:
              primaryEntityActionType ??
              (isActionTypeAlreadyExists ? actionTypeIfItAlreadyExists : pendingChangeActionType),
            entityTypeName,
            parentProperty,
            changeKind: getCombinedPendingChangeKind([
              recordedChangeKind,
              toPendingChangeKind(changeKind),
            ]),
            ...getConditionalObject(isWithAuthors, () => ({
              authors: fieldAuthors[formattedPropertyName],
            })),
          });

          return entityChangesAcc;
        }, {});

        return acc;
      }, {}),
  ),
);

export const selectEntityGroupChangedPropsByIds = memoizeOne(
  ({ partialIds, idPrefix, idPostfix }) => {
    const selectorsArray = partialIds.map(partialId => {
      const combinedId = [idPrefix, partialId, idPostfix].filter(Boolean).join('');

      return selectEntityChangedPropsById(combinedId);
    });

    return createSelector(selectorsArray, (...pendingChanges) => pendingChanges);
  },
  _.isEqual,
);
